import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Target, Plus, CheckCircle, Clock, Calendar, ChevronDown, X, User } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

const PATIENT_ACCOMMODATIONS = {
    1: ['Extended time on tasks', 'Visual schedules', 'AAC device access at all times'],
    2: ['Frequent movement breaks', 'Preferential seating near instructor', 'Chunked task instructions'],
    3: ['Audio-assisted reading tools', 'Colour-coded text overlays', 'Oral response option for exams'],
};

const INITIAL_GOALS_BY_PATIENT = {
    1: [
        {
            id: 1, profileId: 1, domain: 'Communication', title: 'Expressive Language using AAC',
            description: 'Alex will independently request preferred items using a 2-icon sequence.',
            status: 'In Progress', progress: 60, targetDate: 'Dec 2026', strategies: ['Model device usage']
        }
    ],
    2: [
        {
            id: 2, profileId: 2, domain: 'Behavior', title: 'Frustration Regulation',
            description: 'Mia will use a self-regulation strategy (deep breathing) when frustrated, with less than 2 behavioural outbursts per week.',
            status: 'In Progress', progress: 45, targetDate: 'Nov 2026', strategies: ['Visual emotion chart', 'Calm-down corner access']
        }
    ],
    3: [
        {
            id: 3, profileId: 3, domain: 'Academics', title: 'Phonics Fluency Grade 2',
            description: 'Leo will decode CVC and CVCe words with 80% accuracy in a structured reading session.',
            status: 'In Progress', progress: 20, targetDate: 'Jan 2027', strategies: ['Orton-Gillingham multisensory approach']
        }
    ],
};

export default function IEP() {
    const { goals, addGoal, updateGoalProgress, profiles } = useData();
    const [selectedPatientId, setSelectedPatientId] = useState(1);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [progressInputs, setProgressInputs] = useState({});

    const selectedPatient = profiles.find(p => p.id === selectedPatientId) || profiles[0];

    // Merge dynamic goals from DataContext with initial patient-specific goals
    const dynamicGoals = goals.filter(g => g.profileId === selectedPatientId);
    const defaultGoals = INITIAL_GOALS_BY_PATIENT[selectedPatientId] || [];
    // Show default goals that haven't been superseded by dynamic ones with same title
    const dynamicTitles = new Set(dynamicGoals.map(g => g.title));
    const mergedGoals = [
        ...defaultGoals.filter(g => !dynamicTitles.has(g.title)),
        ...dynamicGoals
    ];

    const handleAdd = (e) => {
        e.preventDefault();
        const data = new FormData(e.target);
        addGoal({
            profileId: selectedPatientId,
            domain: data.get('domain'),
            title: data.get('title'),
            description: data.get('description'),
            targetDate: data.get('targetDate'),
            strategies: [data.get('strategy1')]
        });
        setAddModalOpen(false);
        e.target.reset();
    };

    const handleProgressChange = (id, val) => {
        setProgressInputs(p => ({ ...p, [id]: val }));
    };

    const handleLogProgress = (id) => {
        const val = progressInputs[id];
        if (val !== undefined && !isNaN(val)) {
            const num = Math.min(100, Math.max(0, parseInt(val)));
            updateGoalProgress(id, num);
            setProgressInputs(p => ({ ...p, [id]: undefined }));
        }
    };

    const mastered = mergedGoals.filter(g => g.status === 'Mastered').length;
    const atRisk = mergedGoals.filter(g => g.status === 'At Risk').length;
    const totalProgress = mergedGoals.length > 0
        ? Math.round(mergedGoals.reduce((acc, g) => acc + g.progress, 0) / mergedGoals.length)
        : 0;

    const accommodations = PATIENT_ACCOMMODATIONS[selectedPatientId] || [];

    return (
        <div className="max-w-7xl mx-auto space-y-6 pb-12 relative">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Intervention Plan (IEP)</h1>
                    <p className="text-sm text-slate-500">{selectedPatient?.name} • Academic Year 2026-2027</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Patient selector */}
                    <div className="relative">
                        <select
                            value={selectedPatientId}
                            onChange={e => setSelectedPatientId(Number(e.target.value))}
                            className="h-10 pl-4 pr-9 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 appearance-none shadow-sm cursor-pointer"
                        >
                            {profiles.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    <Button variant="outline" className="bg-white" onClick={() => alert('Opening clinical review calendar...')}>
                        <Calendar className="mr-2 h-4 w-4" /> Next Review: Nov 15
                    </Button>
                    <Button onClick={() => setAddModalOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" /> Add Goal
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
                {/* Sidebar Summary */}
                <div className="lg:col-span-1 space-y-6">
                    {/* Patient Info Card */}
                    <Card className="shadow-sm border-t-4 border-t-brand-500">
                        <CardContent className="p-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-sm">
                                    {selectedPatient?.name.split(' ').map(n => n[0]).join('').slice(0,2)}
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 text-sm">{selectedPatient?.name}</p>
                                    <p className="text-xs text-slate-500">{selectedPatient?.diagnosis}</p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                                <span>Age {selectedPatient?.age}</span>
                                <Badge variant={selectedPatient?.status === 'Active Evaluation' ? 'primary' : selectedPatient?.status === 'Discharged' ? 'outline' : 'success'} className="text-[10px]">
                                    {selectedPatient?.status}
                                </Badge>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-base text-slate-800">Plan Overview</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4 divide-y divide-slate-100">
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-slate-500">Active Goals</span>
                                <span className="text-sm font-bold text-slate-900">{mergedGoals.length}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-slate-500">Mastered</span>
                                <span className="text-sm font-bold text-green-600">{mastered}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-slate-500">At Risk</span>
                                <span className="text-sm font-bold text-accent-600">{atRisk}</span>
                            </div>
                            <div className="pt-4 mt-2">
                                <label className="text-xs text-slate-500 font-medium block mb-1">Overall Progress</label>
                                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-brand-500 rounded-full transition-all duration-500" style={{ width: `${totalProgress}%` }}></div>
                                </div>
                                <p className="text-right text-xs font-bold text-brand-700 mt-1">{totalProgress}%</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm">
                        <CardHeader className="pb-3 border-b border-slate-100">
                            <CardTitle className="text-base text-slate-800">Accommodations</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-4">
                            <ul className="space-y-3 text-sm text-slate-700">
                                {accommodations.map((a, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <CheckCircle className="h-4 w-4 text-brand-500 mt-0.5 shrink-0" /> {a}
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                    </Card>
                </div>

                {/* Goals List */}
                <div className="lg:col-span-3 space-y-6">
                    <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                        <h2 className="text-sm font-bold text-slate-900 ml-2">
                            Current Goals — <span className="text-brand-600">{selectedPatient?.name}</span>
                        </h2>
                        <div className="flex gap-2">
                            <Badge variant="outline" className="bg-slate-50 cursor-pointer text-slate-800 border-slate-300">All Domains</Badge>
                        </div>
                    </div>

                    {mergedGoals.length === 0 ? (
                        <Card className="shadow-sm">
                            <CardContent className="p-12 text-center text-slate-400">
                                <Target className="h-10 w-10 mx-auto mb-3 opacity-40" />
                                <p className="text-sm">No goals yet for {selectedPatient?.name}.</p>
                                <Button className="mt-4" size="sm" onClick={() => setAddModalOpen(true)}>
                                    <Plus className="mr-2 h-4 w-4" /> Add First Goal
                                </Button>
                            </CardContent>
                        </Card>
                    ) : mergedGoals.map((goal) => (
                        <Card key={goal.id} className="shadow-sm border-l-4 overflow-hidden hover:shadow-md transition-shadow"
                            style={{ borderLeftColor: goal.status === 'Mastered' ? '#22c55e' : goal.status === 'At Risk' ? '#f59e0b' : '#0ea5e9' }}>
                            <CardContent className="p-0">
                                <div className="p-5 sm:p-6">
                                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-600 border border-slate-200">{goal.domain}</Badge>
                                                <span className="text-xs font-semibold text-slate-400 flex items-center"><Clock className="mr-1 h-3 w-3" /> Target: {goal.targetDate}</span>
                                            </div>
                                            <h3 className="text-lg font-bold text-slate-900 leading-tight">{goal.title}</h3>
                                        </div>
                                        <Badge variant={goal.status === 'Mastered' ? 'success' : goal.status === 'At Risk' ? 'warning' : 'primary'}>
                                            {goal.status}
                                        </Badge>
                                    </div>

                                    <p className="text-sm text-slate-700 mb-6 leading-relaxed bg-brand-50/50 p-4 rounded-lg border border-brand-100/50 italic text-slate-800">
                                        " {goal.description} "
                                    </p>

                                    <div className="grid md:grid-cols-2 gap-6 items-start">
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
                                                <Target className="h-4 w-4 text-slate-400" /> Teaching Strategies
                                            </h4>
                                            <ul className="space-y-2">
                                                {goal.strategies.map((strategy, i) => (
                                                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                                                        <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0"></div>
                                                        {strategy}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="bg-white border text-center border-slate-100 rounded-xl p-4 shadow-sm">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="text-sm font-medium text-slate-700">Progress Tracking</span>
                                                <span className="text-sm font-bold text-brand-700">{goal.progress}%</span>
                                            </div>
                                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden mb-4">
                                                <div className="h-full bg-brand-500 rounded-full transition-all duration-500" style={{ width: `${goal.progress}%` }}></div>
                                            </div>
                                            <div className="flex gap-2 items-center">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    placeholder="0–100"
                                                    className="h-8 text-xs flex-1"
                                                    value={progressInputs[goal.id] ?? ''}
                                                    onChange={e => handleProgressChange(goal.id, e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && handleLogProgress(goal.id)}
                                                />
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 text-xs font-semibold shrink-0"
                                                    onClick={() => handleLogProgress(goal.id)}
                                                >
                                                    Log
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Add Goal Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg shadow-xl border-0">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">Add New Goal</h2>
                                <p className="text-xs text-slate-500 mt-0.5">For: <span className="font-semibold text-brand-700">{selectedPatient?.name}</span></p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setAddModalOpen(false)} className="h-8 w-8 -mr-2"><X className="h-4 w-4" /></Button>
                        </div>
                        <form onSubmit={handleAdd}>
                            <CardContent className="space-y-4 pt-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-1">Domain</label>
                                        <select name="domain" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                                            <option value="Communication">Communication</option>
                                            <option value="Social Skills">Social Skills</option>
                                            <option value="Daily Living">Daily Living</option>
                                            <option value="Motor Skills">Motor Skills</option>
                                            <option value="Academics">Academics</option>
                                            <option value="Behavior">Behavior</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-1">Target Date</label>
                                        <Input name="targetDate" type="date" required />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Goal Title</label>
                                    <Input name="title" required placeholder="e.g. Expressive Language using AAC" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Measurable Description</label>
                                    <textarea name="description" required rows={3} className="w-full rounded-md border border-slate-200 bg-white p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" placeholder="Student will..."></textarea>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Primary Strategy</label>
                                    <Input name="strategy1" required placeholder="e.g. Model device usage" />
                                </div>
                            </CardContent>
                            <div className="p-6 pt-0 flex gap-3 mt-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setAddModalOpen(false)}>Cancel</Button>
                                <Button type="submit" className="flex-1">Add Goal</Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
}
